import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Route, useRouteMatch } from 'react-router-dom'
import BigNumber from 'bignumber.js'
import { useWeb3React } from '@web3-react/core'
import { Heading, useMatchBreakpoints, Text, Card, CardBody, Flex, CopyIcon } from '@pixel-nft/uikit'
import { useTranslation } from 'contexts/Localization'
import usePersistState from 'hooks/usePersistState'
import multicall from 'utils/multicall'
import { usePools } from 'state/hooks'
import FlexLayout from 'components/layout/Flex'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import useRefresh from 'hooks/useRefresh'
import lendingPoolABI from 'config/abi/LendingPool.json'
import useToast from 'hooks/useToast'
import { useERC20, useLendingPool } from 'hooks/useContract'
import { getUSDTAddress, getPixelAddress, getLendingPoolAddress } from 'utils/addressHelpers'
import Page from 'components/layout/Page'
import { BannerStyle, TitleStyle, DescStyle } from './PoolndexStyled'
import LendingPoolCard from './components/LendingPoolCard'

const LendingPools = () => {
  const { path } = useRouteMatch()
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const currentUrl = window.location.href
  const { isSm, isXs, isLg, isMd, isXl } = useMatchBreakpoints()
  const isMobile = isXs || isSm || isLg || isMd
  const refererUrl = `${currentUrl.split('?')[0]}?ref=${account}`
  const displayRefererUrl = isMobile ? `${refererUrl.substring(0, 26)}...` : refererUrl
  const pxfAddress = getPixelAddress()
  const usdtAddress = getUSDTAddress()
  const pxfContract = useERC20(pxfAddress)
  const usdtContract = useERC20(usdtAddress)
  // console.log('usdtContract', usdtContract)
  const lendingPoolAddress = getLendingPoolAddress()
  const lendingPoolContract = useLendingPool()
  // console.log('lendingPoolContract', lendingPoolContract)
  const [pools, setPools] = useState([])
  const [userState, setUserState] = useState({
    pxfAllowance: 0,
    usdtAllowance: 0,
    hasDeposited: false,
    pxfBalance: 0,
    usdtBalance: 0,
  })
  const [userPoolsState, setUserPoolsState] = useState([])
  const { toastSuccess } = useToast()
  const { fastRefresh } = useRefresh()
  // console.log('pools', pools)
  // console.log('userState', userState)
  // console.log('userPoolState', userPoolsState)
  // console.log('fastRefresh', fastRefresh)

  // クッキーの設定関数
  const setCookie = (name, value, days) => {
    let expires = ''
    if (days) {
      const date = new Date()
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
      expires = `; expires=${date.toUTCString()}`
    }
    document.cookie = `${name}=${value || ''}${expires}'; path=/`
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    if (ref) {
      setCookie('ref', ref, 7) // クッキーの有効期限を7日に設定
    }
  }, [])

  const handleCopy = () => {
    toastSuccess(t('Copy Success'), t('Your referer link is copied!'))
  }

  useEffect(() => {
    const fetchUserPools = async () => {
      // Create calls for deposits with index
      const userPoolCounts = await multicall(
        lendingPoolABI,
        pools.map((pool) => {
          return {
            address: lendingPoolAddress,
            name: 'userPoolCounts',
            params: [account, pool.poolId],
          }
        }),
      )
      // console.log('userPoolCounts', userPoolCounts)

      // Execute multicall for pxfDeposits
      const pxfDeposits = await multicall(
        lendingPoolABI,
        pools.map((pool) => {
          return {
            address: lendingPoolAddress,
            name: 'pxfDeposits',
            params: [account, pool.poolId],
          }
        }),
      )
      // console.log('pxfDeposits', pxfDeposits)

      const hasEnoughPXFs = await multicall(
        lendingPoolABI,
        pools.map((pool) => {
          return {
            address: lendingPoolAddress,
            name: 'hasEnoughPXF',
            params: [account, pool.poolId],
          }
        }),
      )
      // console.log('hasEnoughPXFs', hasEnoughPXFs)

      // Execute multicall for totalAmounts
      const calculateRewards = await multicall(
        lendingPoolABI,
        pools.map((pool) => {
          return {
            address: lendingPoolAddress,
            name: 'calculateReward',
            params: [account, pool.poolId],
          }
        }),
      )
      // console.log('calculateRewards', calculateRewards)

      // Execute multicall for totalAmounts
      const poolIsRunnings = await multicall(
        lendingPoolABI,
        pools.map((pool) => {
          return {
            address: lendingPoolAddress,
            name: 'poolIsRunning',
            params: [account, pool.poolId],
          }
        }),
      )
      // console.log('poolIsRunnings', poolIsRunnings)

      // Execute multicall for deposits
      const _depositAmountCalls = []
      pools.forEach((pool) => {
        const _count = userPoolCounts[pool.poolId]
        for (let i = 0; i < _count; i++) {
          _depositAmountCalls.push({
            address: lendingPoolAddress,
            name: 'depositAmounts',
            params: [account, pool.poolId, i],
          })
        }
      })
      const depositAmounts = await multicall(lendingPoolABI, _depositAmountCalls)
      // console.log('depositAmounts', depositAmounts)

      // Execute multicall for deposits
      const _depositTimeCalls = []
      pools.forEach((pool) => {
        const _count = userPoolCounts[pool.poolId]
        for (let i = 0; i < _count; i++) {
          _depositTimeCalls.push({
            address: lendingPoolAddress,
            name: 'depositTimes',
            params: [account, pool.poolId, i],
          })
        }
      })
      const depositTimes = await multicall(lendingPoolABI, _depositTimeCalls)
      // console.log('depositAmounts', depositAmounts)

      // Execute multicall for deposits
      const _isRunningCalls = []
      pools.forEach((pool) => {
        const _count = userPoolCounts[pool.poolId]
        for (let i = 0; i < _count; i++) {
          _isRunningCalls.push({
            address: lendingPoolAddress,
            name: 'isRunnings',
            params: [account, pool.poolId, i],
          })
        }
      })
      const isRunnings = await multicall(lendingPoolABI, _isRunningCalls)
      // console.log('isRunnings', isRunnings)

      // Execute multicall for deposits
      const _canWithdrawCalls = []
      pools.forEach((pool) => {
        const _count = userPoolCounts[pool.poolId]
        for (let i = 0; i < _count; i++) {
          _canWithdrawCalls.push({
            address: lendingPoolAddress,
            name: 'canWithdraw',
            params: [account, pool.poolId, i],
          })
        }
      })
      const canWithdraws = await multicall(lendingPoolABI, _canWithdrawCalls)
      // console.log('canWithdraws', canWithdraws)

      // Aggregate the results
      const results = pools.map((pool) => {
        const _count = userPoolCounts[pool.poolId]
        const _info = []
        for (let i = 0; i < _count; i++) {
          const _depositAmount = depositAmounts.shift()
          const _depositTime = depositTimes.shift()
          const _isRunning = isRunnings.shift()
          const _canWithdraw = canWithdraws.shift()
          _info.push({
            slotId: i,
            depositAmount: _depositAmount[0].toString(),
            depositTime: _depositTime[0].toString(),
            isRunning: _isRunning[0],
            canWithdraw: _canWithdraw[0],
          })
        }

        return {
          poolIsRunning: poolIsRunnings[pool.poolId][0],
          pxfDeposit: pxfDeposits[pool.poolId][0].toString(),
          calculateReward: calculateRewards[pool.poolId][0].toString(),
          hasEnoughPXF: hasEnoughPXFs[pool.poolId][0],
          infos: _info,
        }
      })

      // console.log('results', results)

      setUserPoolsState(results)
    }

    const fetchUser = async () => {
      const hasDeposited = await lendingPoolContract.methods.hasDeposited(account).call()
      const pxfAllowance = await pxfContract.methods.allowance(account, lendingPoolAddress).call()
      const usdtAllowance = await usdtContract.methods.allowance(account, lendingPoolAddress).call()
      const pxfBalance = await pxfContract.methods.balanceOf(account).call()
      // console.log('pxfBalance', pxfBalance)
      const usdtBalance = await usdtContract.methods.balanceOf(account).call()
      // console.log('usdtBalance', usdtBalance)
      setUserState({
        pxfAllowance,
        usdtAllowance,
        hasDeposited,
        pxfBalance,
        usdtBalance,
      })
    }
    // console.log('pools', pools)
    if (account && pools.length > 0) {
      fetchUser()
      fetchUserPools()
    }
  }, [account, pools, lendingPoolContract, pxfContract, usdtContract, lendingPoolAddress])

  useEffect(() => {
    const fetchPools = async () => {
      const length = await lendingPoolContract.methods.getPoolLength().call()
      // console.log('length', length)
      const calls = []
      for (let i = 0; i < length; i++) {
        calls.push({
          address: lendingPoolAddress,
          name: 'pools',
          params: [i],
        })
      }

      // Execute multicall
      const results = await multicall(lendingPoolABI, calls)
      const data = results.map((result) => {
        return {
          poolId: result.poolId.toNumber(),
          annualInterestRate: result.annualInterestRate.toString(),
          lockPeriod: result.lockPeriod.toString(),
          minimumPXF: result.minimumPXF.toString(),
        }
      })
      // console.log('data', data)
      setPools(data)
    }
    fetchPools()
  }, [lendingPoolContract, lendingPoolAddress, pxfContract, usdtContract, fastRefresh])

  return (
    <>
      <BannerStyle isMobile={isMobile}>
        <TitleStyle>LENDING POOLS</TitleStyle>
      </BannerStyle>
      <Page>
        <FlexLayout
          style={{
            justifyContent: isMobile ? 'center' : 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          {pools.map((pool) => (
            <LendingPoolCard
              key={pool.poolId}
              pool={pool}
              account={account}
              userData={userState}
              userPoolData={userPoolsState[pool.poolId]}
            />
          ))}
        </FlexLayout>
        {account && userState.hasDeposited ? (
          <>
            <Card>
              <CardBody style={{ padding: '24px' }}>
                <Flex alignItems="center">
                  <Text fontSize="14px">Referer:</Text>
                  <Text bold style={{ marginLeft: '8px', marginRight: '8px' }}>
                    {displayRefererUrl}
                  </Text>
                  <CopyToClipboard text={refererUrl} onCopy={handleCopy}>
                    <Text fontSize="14px" style={{ cursor: 'pointer', color: '#007bff' }}>
                      <CopyIcon color="primary" width="14px" />
                    </Text>
                  </CopyToClipboard>
                </Flex>
              </CardBody>
            </Card>
          </>
        ) : (
          <></>
        )}
      </Page>
    </>
  )
}

export default LendingPools
